import { useCallback, useSyncExternalStore } from 'react'
import { Settings } from '../../../types'
import { saveSettings as updateSettings } from '../../actions/settings'
import { pubSubSettings } from '../../state/pubSub'
import { SETTINGSSTATE } from '../../state/state.settings'

const getSettings =
    <T extends keyof Settings>(type: T) =>
    () =>
        SETTINGSSTATE[type]

const getSettingSelector =
    <T extends keyof Settings, K extends keyof Settings[T]>(type: T, key: K) =>
    () =>
        SETTINGSSTATE[type][key]

const subSettings =
    <T extends keyof Settings>(type: T) =>
    (callback: () => void) =>
        pubSubSettings.on(type, callback)

export const useSettings = <T extends keyof Settings>(type: T) => {
    const settings = useSyncExternalStore(
        subSettings(type),
        getSettings(type),
        getSettings(type),
    )

    const saveSettings = useCallback(
        <K extends keyof Settings[T]>(key: K, value: Settings[T][K]) =>
            updateSettings(type, key, value),
        [type],
    )

    return [settings, saveSettings] as const
}

export const useSettingSelector = <
    T extends keyof Settings,
    K extends keyof Settings[T],
>(
    type: T,
    key: K,
): [Settings[T][K], (value: Settings[T][K]) => void] => {
    const setting = useSyncExternalStore(
        subSettings(type),
        getSettingSelector(type, key),
        getSettingSelector(type, key),
    )

    const saveSettings = useCallback(
        (value: Settings[T][K]) => updateSettings(type, key, value),
        [type, key],
    )

    return [setting, saveSettings]
}
